/* ERA map. Static geography and ten prepared popularity bands, public schema 3. */
(() => {
  'use strict';
  if (customElements.get('era-community-map')) return;
  const CODES = new Set('AD,AE,AF,AG,AI,AL,AM,AO,AR,AS,AT,AU,AW,AX,AZ,BA,BB,BD,BE,BF,BG,BH,BI,BJ,BL,BM,BN,BO,BQ,BR,BS,BT,BV,BW,BY,BZ,CA,CC,CD,CF,CG,CH,CI,CK,CL,CM,CN,CO,CR,CU,CV,CW,CX,CY,CZ,DE,DJ,DK,DM,DO,DZ,EC,EE,EG,EH,ER,ES,ET,FI,FJ,FK,FM,FO,FR,GA,GB,GD,GE,GF,GG,GH,GI,GL,GM,GN,GP,GQ,GR,GS,GT,GU,GW,GY,HK,HM,HN,HR,HT,HU,ID,IE,IL,IM,IN,IO,IQ,IR,IS,IT,JE,JM,JO,JP,KE,KG,KH,KI,KM,KN,KP,KR,KW,KY,KZ,LA,LB,LC,LI,LK,LR,LS,LT,LU,LV,LY,MA,MC,MD,ME,MF,MG,MH,MK,ML,MM,MN,MO,MP,MQ,MR,MS,MT,MU,MV,MW,MX,MY,MZ,NA,NC,NE,NF,NG,NI,NL,NO,NP,NR,NU,NZ,OM,PA,PE,PF,PG,PH,PK,PL,PM,PN,PR,PS,PT,PW,PY,QA,RE,RO,RS,RU,RW,SA,SB,SC,SD,SE,SG,SH,SI,SJ,SK,SL,SM,SN,SO,SR,SS,ST,SV,SX,SY,SZ,TC,TD,TF,TG,TH,TJ,TK,TL,TM,TN,TO,TR,TT,TV,TW,TZ,UA,UG,UM,US,UY,UZ,VA,VC,VE,VG,VI,VN,VU,WF,WS,YE,YT,ZA,ZM,ZW'.split(','));
  const VIEWS = ['world', 'europe', 'north-america', 'africa'];
  const NS = 'http://www.w3.org/2000/svg';
  const geographyCache = new Map();

  function parseRegions(text) {
    if (typeof text !== 'string' || text.length > 10000) return null;
    try {
      const value = JSON.parse(text);
      if (!value || Array.isArray(value) || typeof value !== 'object') return null;
      if (Object.keys(value).sort().join(',') !== 'regions,schema_version') return null;
      if (value.schema_version !== 3 || !Array.isArray(value.regions)) return null;
      if (value.regions.length > CODES.size) return null;
      if (value.regions.some(region => !region || Array.isArray(region) || typeof region !== 'object' || Object.keys(region).sort().join(',') !== 'band,code' || typeof region.code !== 'string' || !CODES.has(region.code) || !Number.isInteger(region.band) || region.band < 1 || region.band > 10)) return null;
      if (new Set(value.regions.map(region => region.code)).size !== value.regions.length) return null;
      return value.regions;
    } catch { return null; }
  }

  function validateGeography(value) {
    if (!value || value.schema_version !== 2 || value.width !== 960 || value.height !== 960 || !Array.isArray(value.countries)) throw new Error('Invalid geography');
    if (!value.views || VIEWS.some(view => !Array.isArray(value.views[view]) || value.views[view].length !== 4 || value.views[view].some(number => !Number.isFinite(number) || number < 0 || number > 960) || value.views[view][2] <= 0 || value.views[view][3] <= 0)) throw new Error('Invalid geography');
    if (value.countries.length > 300 || value.countries.length < CODES.size) throw new Error('Invalid geography');
    const seen = new Set();
    for (const country of value.countries) {
      if (country.code !== null && (!CODES.has(country.code) || seen.has(country.code))) throw new Error('Invalid geography');
      if (country.code) seen.add(country.code);
      if (typeof country.path !== 'string' || country.path.length > 500000 || /[^MmLlHhVvCcSsQqTtAaZz0-9., +\-]/.test(country.path)) throw new Error('Invalid geography');
    }
    if (seen.size !== CODES.size) throw new Error('Invalid geography');
    return value;
  }

  function loadGeography(source) {
    const url = new URL(source, document.baseURI);
    const shopifyHost = ['cdn.shopify.com', 'cdn.shopifycdn.net'].includes(url.hostname);
    if (!['http:', 'https:'].includes(url.protocol) || (url.origin !== location.origin && !shopifyHost) || !/\/era-community-geography(?:-[a-f0-9]{12})?\.json$/.test(url.pathname)) return Promise.reject(new Error('Invalid source'));
    if (geographyCache.has(url.href)) return geographyCache.get(url.href);
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 10000);
    const promise = fetch(url.href, { credentials: 'omit', signal: abort.signal })
      .then(async response => {
        if (!response.ok) throw new Error('Unavailable geography');
        const body = await response.text();
        if (body.length > 2000000) throw new Error('Invalid geography');
        return validateGeography(JSON.parse(body));
      })
      .catch(error => { geographyCache.delete(url.href); throw error; })
      .finally(() => clearTimeout(timeout));
    geographyCache.set(url.href, promise);
    return promise;
  }

  const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
  function fitCamera(rect, aspect) {
    const width = Math.max(rect[2], rect[3] * aspect);
    const height = width / aspect;
    return [rect[0] + (rect[2] - width) / 2, rect[1] + (rect[3] - height) / 2, width, height];
  }

  class EraCommunityMap extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;
      this.reset();
      const regions = parseRegions(this.querySelector('[data-era-regions]')?.textContent);
      if (!regions?.length) return;
      const status = this.querySelector('[data-unavailable]');
      if (status && this.dataset.loadingText) status.textContent = this.dataset.loadingText;
      loadGeography(this.dataset.geographyUrl).then(geography => {
        if (!this.isConnected || generation !== this.generation) return;
        this.geography = geography;
        this.mapSvg = this.querySelector('.era-community-map__svg');
        this.stage = this.querySelector('[data-stage]') || this.mapSvg;
        const covered = new Map(regions.map(region => [region.code, region.band]));
        this.marks = geography.countries.map(country => {
          const path = document.createElementNS(NS, 'path');
          const band = covered.get(country.code);
          path.setAttribute('class', 'era-community-map__land' + (band ? ` era-community-map__land--covered era-community-map__land--band-${band}` : ''));
          path.setAttribute('d', country.path);
          path.setAttribute('vector-effect', 'non-scaling-stroke');
          if (country.code) path.setAttribute('data-country', country.code);
          if (band) path.setAttribute('data-band', String(band));
          return { path, country };
        });
        const group = this.querySelector('[data-countries]');
        group.replaceChildren(...this.marks.map(mark => mark.path));
        this.listen(this.mapSvg, 'keydown', event => this.onKey(event));
        this.listen(this.mapSvg, 'wheel', event => this.onWheel(event), { passive: false });
        this.listen(this.mapSvg, 'pointerdown', event => this.startDrag(event));
        this.listen(this.mapSvg, 'pointermove', event => this.moveDrag(event));
        for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) this.listen(this.mapSvg, type, event => this.finishPointer(event));
        this.applyPreset();
        this.resizeObserver = new ResizeObserver(() => this.resizeCamera());
        this.resizeObserver.observe(this.stage);
        this.mapSvg.removeAttribute('hidden');
        this.querySelector('[data-legend]')?.removeAttribute('hidden');
        status?.setAttribute('hidden', '');
        const bandLabel = this.dataset.bandLabel || '';
        let names = regions.map(region => `${region.code} (${bandLabel} ${region.band})`);
        try {
          const displayNames = new Intl.DisplayNames([this.dataset.locale || 'en'], { type: 'region' });
          names = regions.map(region => `${displayNames.of(region.code)} (${bandLabel} ${region.band})`).sort((a, b) => a.localeCompare(b, this.dataset.locale || 'en'));
        } catch { /* ISO country codes remain a readable fallback. */ }
        const countryList = this.querySelector('[data-country-list]');
        if (countryList) countryList.textContent = `${this.dataset.countryLabel || ''} ${names.join(', ')}.`;
      }).catch(() => {
        if (this.isConnected && generation === this.generation) this.reset();
      });
    }

    listen(target, type, callback, options) {
      if (!target) return;
      target.addEventListener(type, callback, options);
      this.cleanups.push(() => target.removeEventListener(type, callback, options));
    }

    viewportAspect() {
      const rect = this.stage.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 ? rect.width / rect.height : 960 / 520;
    }

    constrainCamera(rect) {
      const [x, y, width, height] = rect;
      return [
        width >= this.geography.width ? (this.geography.width - width) / 2 : clamp(x, 0, this.geography.width - width),
        height >= this.geography.height ? (this.geography.height - height) / 2 : clamp(y, 0, this.geography.height - height),
        width, height
      ];
    }

    applyPreset() {
      this.endDrag();
      const aspect = this.viewportAspect();
      this.worldCamera = fitCamera(this.geography.views.world, aspect);
      const framed = fitCamera(this.geography.views.europe, aspect);
      this.zoom = clamp(this.worldCamera[2] / framed[2], 1, 6);
      const width = this.worldCamera[2] / this.zoom;
      const height = width / aspect;
      this.camera = this.constrainCamera([framed[0] + framed[2] / 2 - width / 2, framed[1] + framed[3] / 2 - height / 2, width, height]);
      this.paintCamera();
    }

    resizeCamera() {
      if (!this.camera || !this.geography) return;
      const aspect = this.viewportAspect();
      if (Math.abs(this.camera[2] / this.camera[3] - aspect) < 0.001) return;
      this.endDrag();
      this.worldCamera = fitCamera(this.geography.views.world, aspect);
      const width = this.worldCamera[2] / this.zoom;
      const height = width / aspect;
      this.camera = this.constrainCamera([this.camera[0] + this.camera[2] / 2 - width / 2, this.camera[1] + this.camera[3] / 2 - height / 2, width, height]);
      this.paintCamera();
    }

    zoomBy(factor, anchor = [0.5, 0.5]) {
      if (!this.camera) return;
      this.endDrag();
      const next = clamp(this.zoom * factor, 1, 6);
      const width = this.worldCamera[2] / next;
      const height = this.worldCamera[3] / next;
      this.camera = this.constrainCamera([this.camera[0] + (this.camera[2] - width) * anchor[0], this.camera[1] + (this.camera[3] - height) * anchor[1], width, height]);
      this.zoom = next;
      this.paintCamera();
    }

    paintCamera() {
      this.mapSvg.setAttribute('viewBox', this.camera.map(value => Number(value.toFixed(3))).join(' '));
    }

    onWheel(event) {
      if (!this.camera) return;
      event.preventDefault();
      const rect = this.mapSvg.getBoundingClientRect();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1;
      const pixels = event.deltaY * unit;
      this.zoomBy(Math.exp(-clamp(pixels, -250, 250) * 0.0025), [clamp((event.clientX - rect.left) / rect.width, 0, 1), clamp((event.clientY - rect.top) / rect.height, 0, 1)]);
    }

    onKey(event) {
      if (!this.camera || event.altKey || event.ctrlKey || event.metaKey) return;
      const moves = { ArrowLeft: [-0.12, 0], ArrowRight: [0.12, 0], ArrowUp: [0, -0.12], ArrowDown: [0, 0.12] };
      if (moves[event.key]) {
        event.preventDefault();
        this.endDrag();
        const [dx, dy] = moves[event.key];
        this.camera = this.constrainCamera([this.camera[0] + dx * this.camera[2], this.camera[1] + dy * this.camera[3], this.camera[2], this.camera[3]]);
        this.paintCamera();
      } else if (['+', '=', '-', '_', 'Home'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'Home') this.applyPreset();
        else this.zoomBy(event.key === '+' || event.key === '=' ? 1.35 : 1 / 1.35);
      }
    }

    startDrag(event) {
      if (!this.camera || !['mouse', 'touch', 'pen'].includes(event.pointerType) || event.button !== 0 || this.pointers.size >= 2) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.mapSvg.setAttribute('data-panning', '');
      if (event.pointerType === 'mouse') this.mapSvg.focus?.({ preventScroll: true });
      this.mapSvg.setPointerCapture?.(event.pointerId);
      this.beginGesture();
    }

    beginGesture() {
      const points = [...this.pointers.values()];
      if (!points.length || !this.camera) return;
      const rect = this.mapSvg.getBoundingClientRect();
      const center = points.length === 2 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0];
      this.gesture = {
        zoom: this.zoom,
        distance: points.length === 2 ? Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y)) : 1,
        anchor: [this.camera[0] + (center.x - rect.left) / rect.width * this.camera[2], this.camera[1] + (center.y - rect.top) / rect.height * this.camera[3]]
      };
    }

    moveDrag(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      event.preventDefault?.();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const points = [...this.pointers.values()];
      const center = points.length === 2 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0];
      const distance = points.length === 2 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 1;
      this.zoom = clamp(this.gesture.zoom * distance / this.gesture.distance, 1, 6);
      const rect = this.mapSvg.getBoundingClientRect();
      const width = this.worldCamera[2] / this.zoom;
      const height = this.worldCamera[3] / this.zoom;
      this.camera = this.constrainCamera([this.gesture.anchor[0] - (center.x - rect.left) / rect.width * width, this.gesture.anchor[1] - (center.y - rect.top) / rect.height * height, width, height]);
      this.paintCamera();
    }

    finishPointer(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      if (this.mapSvg.hasPointerCapture?.(event.pointerId)) this.mapSvg.releasePointerCapture(event.pointerId);
      if (this.pointers.size) this.beginGesture();
      else {
        this.gesture = null;
        this.mapSvg.removeAttribute('data-panning');
      }
    }

    endDrag() {
      const ids = this.pointers ? [...this.pointers.keys()] : [];
      this.pointers = new Map();
      this.gesture = null;
      this.mapSvg?.removeAttribute('data-panning');
      for (const id of ids) if (this.mapSvg?.hasPointerCapture?.(id)) this.mapSvg.releasePointerCapture(id);
    }

    reset() {
      this.endDrag();
      for (const cleanup of this.cleanups || []) cleanup();
      this.cleanups = [];
      this.resizeObserver?.disconnect();
      this.querySelector('.era-community-map__svg')?.setAttribute('hidden', '');
      this.querySelector('[data-legend]')?.setAttribute('hidden', '');
      const status = this.querySelector('[data-unavailable]');
      if (status) {
        status.removeAttribute('hidden');
        if (this.dataset.unavailableText) status.textContent = this.dataset.unavailableText;
      }
      this.querySelector('[data-countries]')?.replaceChildren();
      const list = this.querySelector('[data-country-list]');
      if (list) list.textContent = '';
      this.marks = [];
      this.camera = null;
      this.geography = null;
    }

    disconnectedCallback() {
      this.generation += 1;
      this.reset();
    }
  }

  customElements.define('era-community-map', EraCommunityMap);
})();
